/**
 * PHASE 7.1 — real browser device-orientation source.
 *
 * Bridges the browser's deviceorientation / deviceorientationabsolute events
 * into the existing OrientationSource duck-type (phase 4). OrientationFusion
 * already subscribes to both event types and ignores all-null readings, so
 * this layer only forwards events verbatim — no filtering, no math, no
 * competing orientation system.
 *
 * The browser surface (window, the DeviceOrientationEvent constructor) is
 * injected as small structural types so the whole module is unit-testable
 * with fakes (same pattern as CameraController / LocationService).
 */

import type { OrientationEventLike, OrientationSource } from "./orientationFusion";

export type MotionPermissionState = "granted" | "denied" | "unavailable";

export interface DeviceOrientationEventCtorLike {
  requestPermission?: unknown;
}

export interface OrientationEventTargetLike {
  addEventListener(type: string, cb: (e: OrientationEventLike) => void): void;
  removeEventListener(type: string, cb: (e: OrientationEventLike) => void): void;
}

export interface DeviceOrientationSourceOptions {
  /** The object that dispatches orientation events (window on a device). */
  target: OrientationEventTargetLike;
}

/**
 * True when the browser at least declares the orientation events on the
 * target. Desktop browsers declare them too, so this only gates ancient or
 * non-supporting browsers; the desktop no-event fallback lives in the shell
 * (main.ts) via `simulatorFallbackDue`.
 */
export function orientationEventsDeclared(target: unknown): boolean {
  if (!target || typeof target !== "object") return false;
  const t = target as Record<string, unknown>;
  return "ondeviceorientation" in t || "ondeviceorientationabsolute" in t;
}

/** True when the browser exposes the iOS-style permission API. */
export function motionPermissionSupported(eventCtor: unknown): boolean {
  if (typeof eventCtor !== "function") return false;
  const ctor = eventCtor as { requestPermission?: unknown };
  return typeof ctor.requestPermission === "function";
}

/**
 * Request motion/orientation permission (iOS Safari). Must be called from a
 * user gesture; on Android/desktop (no API) resolves "unavailable" and the
 * events need no permission. The argument is the DeviceOrientationEvent
 * constructor value (typed `unknown`: the installed lib.dom predates the
 * static requestPermission member, which iOS exposes at runtime).
 */
export function requestMotionPermission(eventCtor: unknown): Promise<MotionPermissionState> {
  if (!motionPermissionSupported(eventCtor)) return Promise.resolve("unavailable");
  try {
    const request = (eventCtor as { requestPermission: () => Promise<unknown> }).requestPermission;
    const result = request();
    return Promise.resolve(result).then(
      (value) => (value === "granted" ? "granted" : "denied"),
      () => "denied",
    );
  } catch {
    // Called outside a user gesture or otherwise refused.
    return Promise.resolve("denied");
  }
}

export interface SimulatorFallbackCondition {
  /** True once the fusion received any reading (real sensors are alive). */
  hasReading: boolean;
  /** True when the motion permission was explicitly denied. */
  permissionDenied: boolean;
  /** True when the motion permission was explicitly granted (but no reading arrived yet). */
  permissionGranted: boolean;
  /** True when the browser gates orientation behind an iOS-style permission prompt. */
  permissionRequestable: boolean;
  /** Device touch points; 0 = desktop-class device. */
  maxTouchPoints: number;
  graceElapsedMs: number;
  /** Grace for touchless devices and after an explicit denial. */
  graceMs: number;
  /** Grace for touch devices that should have working sensors. */
  touchGraceMs: number;
  /** Grace after an explicit grant that never produced a reading. */
  grantedGraceMs: number;
}

/**
 * Decide whether the shell may swap to the simulated orientation source.
 *
 * The simulator must NEVER fight live sensors, so a real reading always wins.
 * The remaining cases are sensor-less devices and broken sensor paths:
 *   - touchless (desktop): swap after `graceMs` of silence
 *   - touch device + explicit denial: swap after `graceMs` (the sensors are
 *     permanently off; give the user the simulator instead of a frozen sky)
 *   - touch device + granted permission but still silent: swap after
 *     `grantedGraceMs` — the sensor path is broken in practice
 *   - iOS-style gated permission, prompt still pending: swap after
 *     `touchGraceMs` (the first tap may still grant permission, but we must
 *     not remain forever with qCamera===null — a frozen sky is worse than a
 *     honest simulated view; the user can still retry permission)
 *   - ungated touch device (Android/desktop Chrome): swap after `touchGraceMs`
 *     (covers sensor-less devices; on real phones readings arrive in ~1s)
 */
export function simulatorFallbackDue(condition: SimulatorFallbackCondition): boolean {
  if (condition.hasReading) return false;
  if (condition.maxTouchPoints === 0) return condition.graceElapsedMs >= condition.graceMs;
  if (condition.permissionDenied) return condition.graceElapsedMs >= condition.graceMs;
  if (condition.permissionGranted) return condition.graceElapsedMs >= condition.grantedGraceMs;
  if (condition.permissionRequestable) return condition.graceElapsedMs >= condition.touchGraceMs;
  return condition.graceElapsedMs >= condition.touchGraceMs;
}

/**
 * Real browser orientation source: forwards deviceorientationabsolute and
 * deviceorientation events from the injected target to the fused listeners.
 * Browser handlers attach on the first listener and detach when idle.
 */
export class DeviceOrientationSource implements OrientationSource {
  private browserTarget: OrientationEventTargetLike;
  private absoluteListeners = new Set<(e: OrientationEventLike) => void>();
  private relativeListeners = new Set<(e: OrientationEventLike) => void>();
  private browserAttached = false;

  constructor(options: DeviceOrientationSourceOptions) {
    this.browserTarget = options.target;
  }

  get available(): boolean {
    return orientationEventsDeclared(this.browserTarget);
  }

  get listenerCount(): number {
    return this.absoluteListeners.size + this.relativeListeners.size;
  }

  addEventListener(type: "deviceorientation" | "deviceorientationabsolute", cb: (e: OrientationEventLike) => void): void {
    const bucket = type === "deviceorientationabsolute" ? this.absoluteListeners : this.relativeListeners;
    bucket.add(cb);
    this.attachBrowser();
  }

  removeEventListener(type: "deviceorientation" | "deviceorientationabsolute", cb: (e: OrientationEventLike) => void): void {
    const bucket = type === "deviceorientationabsolute" ? this.absoluteListeners : this.relativeListeners;
    bucket.delete(cb);
    if (this.absoluteListeners.size === 0 && this.relativeListeners.size === 0) this.detachBrowser();
  }

  private onAbsolute = (e: OrientationEventLike): void => {
    for (const listener of this.absoluteListeners) listener(e);
  };

  private onRelative = (e: OrientationEventLike): void => {
    for (const listener of this.relativeListeners) listener(e);
  };

  private attachBrowser(): void {
    if (this.browserAttached) return;
    this.browserAttached = true;
    this.browserTarget.addEventListener("deviceorientationabsolute", this.onAbsolute);
    this.browserTarget.addEventListener("deviceorientation", this.onRelative);
  }

  private detachBrowser(): void {
    if (!this.browserAttached) return;
    this.browserAttached = false;
    this.browserTarget.removeEventListener("deviceorientationabsolute", this.onAbsolute);
    this.browserTarget.removeEventListener("deviceorientation", this.onRelative);
  }
}